import {gql} from '@apollo/client';
import {naturalSort, partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SourceDAO from 'file-browser/SourceDAO';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {OrganizationPermissionRole, TeamPermissionRole} from 'orgs/OrganizationConstants';
import {PARTY_TYPE_TO_GRAPHQL_TYPENAME} from 'parties/PartyConstants';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import {WHITE} from 'ui-library/Colors';
import {EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch, UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import {Cell, Column, Table, simpleTableStyles} from 'ui-library/simple-table/SimpleTable';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import APIUtil from 'util/APIUtil';
import strUtil from 'util/strUtil';
import {ElementMouseEvent, InputChangeEvent} from 'util/Types';

import {CollaborationManagementContentDataPoliciesQuery} from './__generated/CollaborationManagementContentDataPoliciesQuery';
import {
  CollaborationManagementStateQuery,
  CollaborationManagementStateQuery_policeable_AaSequenceNode_inheritedCollaborations_CollaborationNode_party_BenchlingAppNode_ServiceAccountActorNode_UserNode as Principal,
  CollaborationManagementStateQuery_policeable_AaSequenceNode_inheritedCollaborations_CollaborationNode_party_OrganizationNode_TeamNode as Group,
} from './__generated/CollaborationManagementStateQuery';
import {
  OverflowMenuCell,
  PartyCell,
  PolicyDropdown,
  InheritedPolicyInfo,
  NONE,
  OwnerPolicyInfo,
} from './CollaborationRow';
import {Collaboration, Policeable} from './useCollaborationManagementState';
type Party = ExtractByTypename<
  CollaborationManagementStateQuery['policeable'],
  'ConnectionSchemaNode'
>['inheritedCollaborations'][number]['party'];

export interface Policy {
  __typename: 'SchemaPolicyNode' | 'DataPolicyNode' | 'PolicyNode';
  id: string;
  name: string;
  isHidden: boolean;
}

export const COLLABORATION_MANAGEMENT_CONTENT_DATA_POLICIES_QUERY = gql`
  query CollaborationManagementContentDataPoliciesQuery @skipAudit {
    currentTenant {
      id
      dataPolicies {
        id
        name
        isHidden
      }
    }
  }
`;

export function DataPolicyCollaborationManagementContent(
  props: Omit<CollaborationManagementContentProps, 'policies' | 'allowEditableOwner'>
): JSX.Element {
  return (
    <BenchlingQuery<CollaborationManagementContentDataPoliciesQuery>
      query={COLLABORATION_MANAGEMENT_CONTENT_DATA_POLICIES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({currentTenant}) => (
        <CollaborationManagementContent
          {...props}
          policies={currentTenant.dataPolicies}
          allowEditableOwner={false}
        />
      )}
    </BenchlingQuery>
  );
}

export interface CollaborationManagementContentProps {
  policeable?: Policeable;
  collaborations: Array<Collaboration>;
  ancestorCollaborations: Array<Collaboration>;
  setCollaborations: (collaborations: Array<Collaboration>) => void;
  renderAuditorColumn?: boolean;
  onLeave?: (c: Collaboration) => Promise<void>;
  isReadOnly: boolean;
  policies: Array<Policy>;
  /**
   * Policy IDs that are not offered as choices. This avoids suggesting policies that are not
   * applicable rather than enforcing whether they may be selected. A policy remains visible when
   * already selected, such as for an unchangeable owner policy.
   */
  unavailablePolicyIds?: Array<string>;
  modalRender?: boolean;
  allowEditableOwner: boolean;
  setCanSave: (canSave: boolean) => void;
}

export default function CollaborationManagementContent(
  props: CollaborationManagementContentProps
): JSX.Element {
  return (
    <div
      className={css(styles.collaborationManagementContent)}
      {...getTestProps({component: 'CollaborationManagementContent', element: 'root'})}
    >
      <CollaborationSearchRow
        policeable={props.policeable}
        collaborations={props.collaborations}
        setCollaborations={props.setCollaborations}
        policies={props.policies}
        unavailablePolicyIds={props.unavailablePolicyIds}
        isReadOnly={props.isReadOnly}
        setCanSave={props.setCanSave}
      />
      <CollaborationList
        policeable={props.policeable}
        collaborations={props.collaborations}
        ancestorCollaborations={props.ancestorCollaborations}
        setCollaborations={props.setCollaborations}
        renderAuditorColumn={props.renderAuditorColumn ?? false}
        policies={props.policies}
        unavailablePolicyIds={props.unavailablePolicyIds}
        isReadOnly={props.isReadOnly}
        onLeave={props.onLeave}
        modalRender={props.modalRender}
        allowEditableOwner={props.allowEditableOwner}
      />
    </div>
  );
}

interface CollaborationSearchRowProps {
  policeable?: Policeable;
  collaborations: Array<Collaboration>;
  setCollaborations: (collaborations: Array<Collaboration>) => void;
  policies: Array<Policy>;
  unavailablePolicyIds?: Array<string>;
  isReadOnly: boolean;
  setCanSave: (canSave: boolean) => void;
}

export function CollaborationSearchRow(props: CollaborationSearchRowProps): JSX.Element {
  const policies = props.policies.filter((p: Policy) => !p.isHidden);
  const availablePolicies = policies.filter((policy) => !props.unavailablePolicyIds?.includes(policy.id));
  // NOTE: The default policies are not deletable so every tenant has at least one non-hidden policy.
  const [policy, setPolicy] = React.useState<Policy>(availablePolicies[0]);
  const [pendingCollaborators, _setPendingCollaborators] = React.useState<Array<EntityWithTextAndSuspended>>(
    []
  );
  const setPendingCollaborators = (collaborators: Array<EntityWithTextAndSuspended>): void => {
    _setPendingCollaborators(collaborators);
    props.setCanSave(collaborators.length === 0);
  };
  const existingCollaboratorIds = [...props.collaborations.map((c) => c.party), ...pendingCollaborators].map(
    (c: {id: string}) => c.id
  );
  const allowInvites =
    props.policeable &&
    APIUtil.apiIdToType(props.policeable.id) === ModelTypeConstants.SOURCE &&
    BenchlingConfig.ENABLE_USER_SIGNUPS;
  const inviteCollaborator = (email: string): void => {
    SourceDAO.addCollaborator(props.policeable!.id, {entity_id: email})
      .done(() => {
        notifications.success(`Successfully invited ${email}`);
      })
      .fail(notifications.notifyError);
  };
  const handleSelectOption = (options: Array<EntityWithTextAndSuspended>): void => {
    const optionsWithHandle = options.filter((opt) => opt.handle);
    const optionsWithoutHandle = options.filter((opt) => !opt.handle);
    setPendingCollaborators(optionsWithHandle);
    if (allowInvites) {
      optionsWithoutHandle.forEach((option) => {
        if (
          (option.text && strUtil.isValidEmail(option.text)) ||
          (option.email && strUtil.isValidEmail(option.email))
        ) {
          inviteCollaborator(option.email || option.text);
        }
      });
    }
  };
  return (
    <div className="u-flex-display" {...getTestProps({component: 'CollaborationSearchRow', element: 'root'})}>
      <EntityInputWithSearch
        className="u-flex-grow u-tiny-padding-right"
        allowTeams={true}
        allowEmails={allowInvites}
        allowBenchlingApps={true}
        allowOrganizations={true}
        emptyText="No matches found."
        enableNewEntityInput={true}
        shouldAllowMultiple={true}
        size={TagsInputSize.MEDIUM}
        userSearchType={UserSearchType.NotSuspended}
        value={pendingCollaborators}
        onChange={handleSelectOption}
        entityInputChipSize={ChipSize.MINI}
        disableOptionsFilter={(tag: string, collaborator: EntityWithTextAndSuspended) =>
          existingCollaboratorIds.includes(collaborator.id)
        }
        disabled={props.isReadOnly}
      />
      {/* TODO(jeremyv): The static button size causes long policy names to overflow the button. */}
      <div className={classNames(css(styles.policyDropdown), 'u-tiny-padding-right')}>
        <PolicyDropdown
          policies={policies}
          unavailablePolicyIds={props.unavailablePolicyIds}
          policyId={policy.id}
          allowNone={false}
          isReadOnly={props.isReadOnly}
          buttonSize={ButtonSize.MEDIUM}
          onChangePolicy={(policyId: string) => {
            // policyId must always be in policies.
            setPolicy(policies.find((p: Policy) => p.id === policyId)!);
          }}
        />
      </div>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        text="Add"
        disabled={props.isReadOnly || pendingCollaborators.length === 0}
        onClick={(e: ElementMouseEvent) => {
          const updatedCollaborations = [
            ...props.collaborations,
            ...pendingCollaborators.map((entity: EntityWithTextAndSuspended): Collaboration => {
              const party =
                entity.type === ModelTypeConstants.ORGANIZATION || entity.type === ModelTypeConstants.TEAM
                  ? ({
                      __typename: PARTY_TYPE_TO_GRAPHQL_TYPENAME[entity.type] as
                        | 'OrganizationNode'
                        | 'TeamNode',
                      id: entity.id,
                      name: entity.name,
                      avatarUrl: entity.avatar_url,
                      admin: entity.admin,
                      handle: entity.handle,
                    } satisfies Group)
                  : ({
                      __typename: PARTY_TYPE_TO_GRAPHQL_TYPENAME[entity.type] as
                        | 'UserNode'
                        | 'BenchlingAppNode',
                      id: entity.id,
                      name: entity.name,
                      avatarUrl: entity.avatar_url,
                      handle: entity.handle,
                    } satisfies Principal);
              return {
                __typename: 'CollaborationNode',
                party,
                policy,
                organizationRole:
                  entity.type === ModelTypeConstants.ORGANIZATION ? OrganizationPermissionRole.MEMBER : null,
                teamRole: entity.type === ModelTypeConstants.TEAM ? TeamPermissionRole.MEMBER : null,
                auditor: false,
                resource:
                  props.policeable != null
                    ? {
                        __typename: 'RestrictedResourceNode',
                        id: props.policeable.id,
                        name: props.policeable.name,
                        type: '',
                      }
                    : null,
                fromOwnership: false,
              };
            }),
          ];
          props.setCollaborations(updatedCollaborations);
          setPendingCollaborators([]);
        }}
        dataTestAttrs={{component: 'CollaborationSearchRow', element: 'addCollaboratorButton'}}
      />
    </div>
  );
}

export const NONE_POLICY: Policy = {
  __typename: 'DataPolicyNode',
  id: NONE,
  name: NONE,
  isHidden: false,
};

interface CollaborationListProps {
  policeable?: Policeable;
  collaborations: Array<Collaboration>;
  ancestorCollaborations: Array<Collaboration>;
  setCollaborations: (collaborations: Array<Collaboration>) => void;
  renderAuditorColumn: boolean;
  policies: Array<Policy>;
  unavailablePolicyIds?: Array<string>;
  isReadOnly: boolean;
  onLeave?: (c: Collaboration) => Promise<void>;
  modalRender?: boolean;
  allowEditableOwner: boolean;
}

function computeCollaboratorKey(collaboration: Collaboration): string {
  const groupRole = collaboration.teamRole ?? collaboration.organizationRole;
  // Create a key for a collaboration that we can use to group and then sort the them. It uses the
  // following key parts:
  // 1. The name of the party which is mainly used for sorting.
  // 2. The ID of the party for uniqueness (two parties can have the same name).
  // 3. The role of the party if it is a group so that the member role is sorted before the admin role.
  return `${collaboration.party.name}:${collaboration.party.id}:${
    groupRole != null ? -1 * groupRole : groupRole
  }`;
}

function collaborationWithRole(
  collaboration: Collaboration,
  role: TeamPermissionRole | OrganizationPermissionRole
): Collaboration {
  return {
    ...collaboration,
    teamRole: collaboration.teamRole == null ? collaboration.teamRole : role,
    organizationRole: collaboration.organizationRole == null ? collaboration.organizationRole : role,
  };
}

function addMissingMemberCollaborations(collaborations: Array<Collaboration>): Array<Collaboration> {
  const allKeys = new Set(collaborations.map((c: Collaboration) => computeCollaboratorKey(c)));
  return collaborations.reduce((arr: Array<Collaboration>, collaboration: Collaboration) => {
    arr.push(collaboration);
    if ((collaboration.teamRole ?? collaboration.organizationRole) === OrganizationPermissionRole.ADMIN) {
      const memberNoneCollaboration = {
        ...collaborationWithRole(collaboration, OrganizationPermissionRole.MEMBER),
        policy: NONE_POLICY,
      };
      if (!allKeys.has(computeCollaboratorKey(memberNoneCollaboration))) {
        arr.push(memberNoneCollaboration);
      }
    }
    return arr;
  }, []);
}

export function CollaborationList(props: CollaborationListProps): JSX.Element {
  const directCollaborations = addMissingMemberCollaborations(props.collaborations);
  const allCollaborations = addMissingMemberCollaborations([
    ...directCollaborations,
    ...props.ancestorCollaborations,
  ]);
  const collaborationsByCollaborator = allCollaborations.reduce(
    (map: Map<string, Array<Collaboration>>, collaboration: Collaboration) => {
      const collaboratorKey = computeCollaboratorKey(collaboration);
      if (!map.has(collaboratorKey)) {
        map.set(collaboratorKey, []);
      }
      map.get(collaboratorKey)!.push(collaboration);
      return map;
    },
    new Map<string, Array<Collaboration>>()
  );
  const directCollaborationsByCollaborator = directCollaborations.reduce(
    (map: Map<string, Collaboration>, collaboration: Collaboration) => {
      map.set(computeCollaboratorKey(collaboration), collaboration);
      return map;
    },
    new Map<string, Collaboration>()
  );
  const updateCollaboration = (c: Collaboration): void => {
    props.setCollaborations([
      ...props.collaborations.filter(
        (ec: Collaboration) => computeCollaboratorKey(ec) !== computeCollaboratorKey(c)
      ),
      c,
    ]);
  };
  const removeCollaborations = (clbs: Array<Collaboration>): void => {
    const clbKeys = clbs.map((c) => computeCollaboratorKey(c));
    props.setCollaborations(
      props.collaborations.filter((ec: Collaboration) => !clbKeys.includes(computeCollaboratorKey(ec)))
    );
  };
  const [hasOwnerCollaborators, onlyHasNonOwners] = partition(
    [...collaborationsByCollaborator.keys()],
    (key: string): boolean =>
      // Find if this collaborator has any ownership collaborations.
      collaborationsByCollaborator
        .get(key)!
        .some((collaboration: Collaboration): boolean => collaboration.fromOwnership ?? false)
  );
  const [hasDirectCollaborators, onlyIndirectCollaborators] = partition(
    onlyHasNonOwners,
    (key: string): boolean =>
      // Find if this collaborator has any direct collaborations.
      directCollaborationsByCollaborator.has(key) ||
      // Find if alternative roles for this collaborator has any direct collaborations.
      collaborationsByCollaborator.get(key)!.some((collaboration: Collaboration): boolean => {
        const adminRoleCollaboration = computeCollaboratorKey(
          collaborationWithRole(collaboration, OrganizationPermissionRole.ADMIN)
        );
        const memberRoleCollaboration = computeCollaboratorKey(
          collaborationWithRole(collaboration, OrganizationPermissionRole.MEMBER)
        );
        return (
          directCollaborationsByCollaborator.has(adminRoleCollaboration) ||
          directCollaborationsByCollaborator.has(memberRoleCollaboration)
        );
      })
  );
  const sortedHasOwnerCollaborators = naturalSort(hasOwnerCollaborators);
  const sortedHasDirectCollaborators = naturalSort(hasDirectCollaborators);
  const sortedOnlyIndirectCollaborators = naturalSort(onlyIndirectCollaborators);
  const sortedCollaboratorKeys = [
    ...sortedHasOwnerCollaborators,
    ...sortedHasDirectCollaborators,
    ...sortedOnlyIndirectCollaborators,
  ];
  return (
    <div className={css(styles.scrollContainer, props.modalRender && styles.scrollContainerModal)}>
      <Table
        className={css(props.modalRender && styles.tableModal)}
        numRows={sortedCollaboratorKeys.length}
        frozenHeader={false}
        overflowVisible={true}
        showBorder={false}
        getRowClassName={(i: number) => {
          const collaboration = collaborationsByCollaborator.get(sortedCollaboratorKeys[i])![0];
          return (collaboration.organizationRole ?? collaboration.teamRole) ===
            OrganizationPermissionRole.ADMIN
            ? css(styles.adminRoleRow)
            : '';
        }}
        tableLayout="fixed"
      >
        <Column
          header={<CustomHeaderCell className={css(styles.largeCell)}>Collaborators</CustomHeaderCell>}
          renderCell={(i: number) =>
            renderPartyCell(collaborationsByCollaborator.get(sortedCollaboratorKeys[i])![0])
          }
        />
        <Column
          header={<CustomHeaderCell className={css(styles.largeCell)}>Access Policies</CustomHeaderCell>}
          renderCell={(i: number) =>
            renderAccessPoliciesCell(
              directCollaborationsByCollaborator.get(sortedCollaboratorKeys[i]),
              collaborationsByCollaborator.get(sortedCollaboratorKeys[i])!,
              directCollaborationsByCollaborator,
              updateCollaboration,
              removeCollaborations,
              props.policies,
              props.unavailablePolicyIds,
              props.isReadOnly,
              props.allowEditableOwner
            )
          }
        />
        {props.renderAuditorColumn && (
          <Column
            header={<CustomHeaderCell className={css(styles.auditorCell)}>Auditor</CustomHeaderCell>}
            renderCell={(i: number) =>
              renderAuditorCell(
                directCollaborationsByCollaborator.get(sortedCollaboratorKeys[i]),
                updateCollaboration,
                props.isReadOnly
              )
            }
          />
        )}
        <Column
          header={<CustomHeaderCell className={css(styles.overflowCell)} />}
          renderCell={(i: number) =>
            renderOverflowMenuCell({
              collaboratorKey: sortedCollaboratorKeys[i],
              directCollaborationsByCollaborator,
              isReadOnly: props.isReadOnly,
              removeCollaborations,
              updateCollaboration,
              onLeave: props.onLeave,
            })
          }
        />
      </Table>
    </div>
  );
}

function CustomHeaderCell({children, className}: {children?: string; className?: string}): JSX.Element {
  return (
    <Cell className={classNames(css(simpleTableStyles.frozenHeader, styles.headerCell), className)}>
      <div>{children}</div>
    </Cell>
  );
}

function renderPartyCell(collaboration: Collaboration): JSX.Element {
  return (
    <Cell className={css(styles.largeCell)}>
      <PartyCell
        party={collaboration.party}
        groupRole={collaboration.teamRole ?? collaboration.organizationRole ?? undefined}
      />
    </Cell>
  );
}

function renderAccessPoliciesCell(
  collaboration: Collaboration | undefined,
  collaborations: Array<Collaboration>,
  directCollaborationsByCollaborator: Map<string, Collaboration>,
  updateCollaboration: (c: Collaboration) => void,
  removeCollaborations: (clbs: Array<Collaboration>) => void,
  policies: Array<Policy>,
  unavailablePolicyIds: Array<string> | undefined,
  isReadOnly: boolean,
  allowEditableOwner: boolean
): JSX.Element {
  return (
    <Cell className={css(styles.largeCell)}>
      {collaboration && (
        <PolicyDropdown
          policies={policies.filter((p: Policy) => !p.isHidden)}
          unavailablePolicyIds={unavailablePolicyIds}
          policyId={collaboration.policy.id}
          allowNone={
            (collaboration.organizationRole ?? collaboration.teamRole) ===
              OrganizationPermissionRole.MEMBER &&
            (directCollaborationsByCollaborator.has(
              computeCollaboratorKey(collaborationWithRole(collaboration, OrganizationPermissionRole.ADMIN))
            ) ||
              Boolean(collaboration.fromOwnership))
          }
          isReadOnly={
            isReadOnly ||
            (collaboration.fromOwnership === true &&
              collaboration.organizationRole !== OrganizationPermissionRole.MEMBER &&
              !allowEditableOwner)
          }
          onChangePolicy={(policyId: string) => {
            if (policyId === NONE) {
              removeCollaborations([collaboration]);
              return;
            }
            // policyId must always be in policies.
            updateCollaboration({
              ...collaboration,
              policy: policies.find((p: Policy) => p.id === policyId)!,
            });
          }}
        />
      )}
      {collaboration?.fromOwnership &&
        collaboration.organizationRole !== OrganizationPermissionRole.MEMBER && (
          <OwnerPolicyInfo policeable={collaboration.resource} hasTopSpacing={true} />
        )}
      {collaborations
        .filter((c: Collaboration) => c !== collaboration)
        .map((c: Collaboration, index: number): JSX.Element => {
          if (c.fromOwnership && c.organizationRole !== OrganizationPermissionRole.MEMBER) {
            return (
              <OwnerPolicyInfo
                policeable={c.resource}
                key={getAccessibleOrInaccessibleId(c.resource!)}
                hasTopSpacing={collaboration != null}
                policyName={c.policy.name}
              />
            );
          }
          return (
            <InheritedPolicyInfo
              hasTopSpacing={collaboration != null || index > 0}
              key={getAccessibleOrInaccessibleId(c.resource!)}
              policyName={c.policy.name}
              policeable={c.resource!}
            />
          );
        })}
    </Cell>
  );
}

function renderAuditorCell(
  collaboration: Collaboration | undefined,
  updateCollaboration: (c: Collaboration) => void,
  isReadOnly: boolean
): JSX.Element {
  const isActor = (collaboration?.teamRole ?? collaboration?.organizationRole) == null;
  const isTeamMemberRole = collaboration?.teamRole === TeamPermissionRole.MEMBER;
  const isAuditor = Boolean(collaboration?.auditor);
  return (
    <Cell className={css(styles.auditorCell)}>
      {collaboration && (isActor || isTeamMemberRole || isAuditor) && (
        <CheckboxInput
          className={css(styles.auditorCheckbox)}
          id="auditor-checkbox"
          label=""
          checked={collaboration.auditor ?? false}
          onChange={(e: InputChangeEvent) => {
            updateCollaboration({...collaboration, auditor: e.target.checked});
          }}
          disabled={isReadOnly}
        />
      )}
    </Cell>
  );
}

const _canLeave = (party: Party): boolean => {
  const currentUser = EntityStore.currentUser();
  if (!currentUser) {
    return false;
  }
  return (
    party.id === currentUser.id ||
    ((party.__typename === 'OrganizationNode' || party.__typename === 'TeamNode') && party.admin)
  );
};

interface RenderOverflowMenuCellProps {
  collaboratorKey: string;
  directCollaborationsByCollaborator: Map<string, Collaboration>;
  isReadOnly: boolean;
  removeCollaborations: (clbs: Array<Collaboration>) => void;
  updateCollaboration: (c: Collaboration) => void;
  onLeave?: (c: Collaboration) => Promise<void>;
}

function renderOverflowMenuCell(props: RenderOverflowMenuCellProps): JSX.Element {
  const collaboration = props.directCollaborationsByCollaborator.get(props.collaboratorKey);
  if (collaboration == null || collaboration.fromOwnership) {
    return <Cell className={css(styles.overflowCell)} />;
  }

  const groupRole = collaboration?.teamRole ?? collaboration?.organizationRole ?? undefined;
  const adminCollaboration = collaborationWithRole(collaboration, OrganizationPermissionRole.ADMIN);
  const adminPolicyExists =
    groupRole != null &&
    props.directCollaborationsByCollaborator.get(computeCollaboratorKey(adminCollaboration)) != null;

  const menuOptions: Array<OverflowMenuOption> = [
    ...(groupRole == null
      ? [
          {
            onSelect: () => {
              props.removeCollaborations([collaboration]);
            },
            contents: <span>Remove</span>,
          },
        ]
      : []),
    ...(groupRole === OrganizationPermissionRole.MEMBER
      ? [
          {
            onSelect: () => {
              props.removeCollaborations([collaboration, adminCollaboration]);
            },
            contents: (
              <span>Remove {collaboration.party.__typename === 'TeamNode' ? 'team' : 'organization'}</span>
            ),
          },
          {
            onSelect: () => {
              props.updateCollaboration(adminCollaboration);
            },
            contents: <span>Add admin member access policy</span>,
            isDisabled: adminPolicyExists,
          },
        ]
      : []),
    ...(groupRole === OrganizationPermissionRole.ADMIN
      ? [
          {
            onSelect: () => {
              props.removeCollaborations([collaboration]);
            },
            contents: <span>Remove admin member access policy</span>,
          },
        ]
      : []),
  ];

  const readOnlyMenuOptions: Array<OverflowMenuOption> =
    props.onLeave && _canLeave(collaboration.party)
      ? [
          {
            onSelect: () => {
              props.onLeave!(collaboration);
              props.removeCollaborations([collaboration, adminCollaboration]);
            },
            contents: <span>Leave</span>,
          },
        ]
      : [];
  return (
    <Cell className={css(styles.overflowCell)}>
      <OverflowMenuCell
        menuOptions={props.isReadOnly ? readOnlyMenuOptions : menuOptions}
        isDirectCollaborator={collaboration != null}
      />
    </Cell>
  );
}

const NUM_LARGE_CELLS = 2;
const AUDITOR_CELL_WIDTH = 60;
const OVERFLOW_CELL_WIDTH = 50;
const styles = StyleSheet.create({
  policyDropdown: {
    width: 150,
  },
  tableModal: {
    height: 300,
  },
  scrollContainer: {
    overflowY: 'scroll',
    overflowX: 'hidden',
  },
  scrollContainerModal: {
    marginBottom: -16,
    height: '100%',
  },
  headerCell: {
    background: WHITE,
  },
  largeCell: {
    // Distribute remaining width amongst the large cells
    width: `calc(100% / ${NUM_LARGE_CELLS} - ${
      (AUDITOR_CELL_WIDTH + OVERFLOW_CELL_WIDTH) / NUM_LARGE_CELLS
    }px)`,
    whiteSpace: 'pre-wrap',
    verticalAlign: 'top',
  },
  auditorCell: {
    width: AUDITOR_CELL_WIDTH,
    verticalAlign: 'top',
  },
  auditorCheckbox: {
    justifyContent: 'center',
  },
  overflowCell: {
    width: OVERFLOW_CELL_WIDTH,
  },
  adminRoleRow: {
    boxShadow: 'none',
  },
  collaborationManagementContent: {
    height: '100%',
  },
});
